import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, AlertTriangle, ShieldAlert, ExternalLink } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BILLING_PAGE } from "@/constants/testIds";
import { toast } from "sonner";

const STATUS_LABELS = {
  no_subscription: { label: "Sin suscripción activa", color: "text-charcoal/60 bg-charcoal/5" },
  active: { label: "Suscripción activa", color: "text-emerald-700 bg-emerald-50" },
  grace_period: { label: "Pago pendiente — periodo de gracia", color: "text-amber-700 bg-amber-50" },
  read_only: { label: "Solo lectura — actualiza tu pago", color: "text-red-700 bg-red-50" },
  canceled: { label: "Suscripción cancelada", color: "text-red-700 bg-red-50" },
};

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [declaredTier, setDeclaredTier] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const load = () => api.get("/billing/status").then(({ data }) => setStatus(data));

  useEffect(() => {
    load();
    const checkout = searchParams.get("checkout");
    if (checkout === "success") toast.success("Pago procesado. Puede tardar unos segundos en reflejarse.");
    if (checkout === "cancel") toast.info("Checkout cancelado, no se realizó ningún cargo.");
  }, [searchParams]);

  // La banda solo se declara a mano en el pago inicial (billing_status "no_subscription"),
  // cuando por conteo real de empleados siempre tocaría la banda más barata porque todavía no
  // se ha cargado a nadie. Después del primer pago, la banda sube sola conforme se agregan
  // empleados (ver /billing/tier-preview en el alta de empleados).
  useEffect(() => {
    if (status?.billing_status === "no_subscription") {
      api.get("/billing/plans").then(({ data }) => setPlans(data.plans.filter((p) => !p.contact_only)));
    }
  }, [status?.billing_status]);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const payload = { billing_cycle: cycle, origin_url: window.location.origin };
      if (status?.billing_status === "no_subscription" && declaredTier) payload.declared_plan_tier = declaredTier;
      const { data } = await api.post("/billing/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error("No se pudo abrir el portal de facturación.");
      setLoading(false);
    }
  };

  const statusInfo = status ? STATUS_LABELS[status.billing_status] || STATUS_LABELS.no_subscription : null;

  return (
    <AdminLayout>
      <div className="max-w-md">
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight flex items-center gap-3">
          <CreditCard className="w-7 h-7 text-petroleum" strokeWidth={1.5} /> Facturación
        </h1>
        <p className="text-charcoal/60 mt-1">Tu plan se ajusta según los empleados activos de tu empresa.</p>

        {status?.billing_status === "grace_period" && (
          <div data-testid={BILLING_PAGE.gracePeriodBanner} className="mt-6 bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
            <p className="text-sm text-amber-800">
              Tuvimos un problema al cobrar tu suscripción. Tienes {status.grace_period_days} días de gracia con acceso
              completo — actualiza tu método de pago desde "Administrar suscripción" para evitar pasar a solo lectura.
            </p>
          </div>
        )}
        {(status?.billing_status === "read_only" || status?.billing_status === "canceled") && (
          <div data-testid={BILLING_PAGE.readOnlyBanner} className="mt-6 bg-red-50 border border-red-300 rounded-xl p-4 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" strokeWidth={1.5} />
            <p className="text-sm text-red-800">
              Tu cuenta está en modo solo lectura. Puedes ver toda tu información, pero no crear ni modificar registros
              hasta que se resuelva el pago.
            </p>
          </div>
        )}

        {status && (
          <div data-testid={BILLING_PAGE.currentPlanCard} className="mt-6 bg-card border border-petroleum/10 rounded-xl p-5">
            <span data-testid={BILLING_PAGE.statusBadge} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
            <p className="font-heading text-2xl font-bold text-charcoal mt-3">{status.plan_tier?.label ?? "Sin plan asignado"}</p>
            <p className="text-sm text-charcoal/60 mt-1">
              {status.plan_tier?.contact_only ? "Plan a medida" : status.plan_tier?.monthly_price != null ? `$${status.plan_tier.monthly_price.toLocaleString("es-MX")} MXN/mes` : "—"} · {status.active_employee_count} empleado(s) activo(s)
              {status.billing_cycle && <> · ciclo {status.billing_cycle === "annual" ? "anual" : "mensual"}</>}
            </p>

            {status.plan_tier?.contact_only ? (
              <a href="mailto:contacto@cerh.com.mx?subject=Plan%2051%2B%20empleados" className="inline-block mt-5 text-sm font-semibold bg-charcoal text-white px-4 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all">
                Contáctanos para tu plan
              </a>
            ) : status.has_subscription ? (
              <Button data-testid={BILLING_PAGE.manageButton} onClick={openPortal} disabled={loading} className="mt-5 bg-petroleum rounded-full">
                Administrar suscripción <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            ) : status.billing_status !== "no_subscription" ? (
              <p data-testid={BILLING_PAGE.demoAccountNote} className="mt-5 text-sm text-charcoal/50">
                Esta es una cuenta de demostración, no tiene una suscripción real asociada.
              </p>
            ) : (
              <div className="mt-5">
                {status.billing_status === "no_subscription" && (
                  <div className="mb-3">
                    <label className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide">¿Cuántos empleados vas a manejar?</label>
                    <select
                      data-testid="billing-declared-tier-select"
                      required
                      value={declaredTier}
                      onChange={(e) => setDeclaredTier(e.target.value)}
                      className="w-full mt-1 border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm"
                    >
                      <option value="">Selecciona tu banda</option>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>{p.label} — ${p.monthly_price.toLocaleString("es-MX")} MXN/mes</option>
                      ))}
                    </select>
                    <p className="text-xs text-charcoal/50 mt-1">Si más adelante cargas más empleados de los que declaraste aquí, tu banda sube sola y te avisamos antes de que pase.</p>
                  </div>
                )}
                <select
                  data-testid={BILLING_PAGE.cycleSelect}
                  value={cycle}
                  onChange={(e) => setCycle(e.target.value)}
                  className="w-full border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm mb-3"
                >
                  <option value="monthly">Mensual</option>
                  <option value="annual">Anual (10% de descuento)</option>
                </select>
                <Button data-testid={BILLING_PAGE.subscribeButton} onClick={startCheckout} disabled={loading || (status.billing_status === "no_subscription" && !declaredTier)} className="w-full bg-terracotta rounded-full">
                  {loading ? "Redirigiendo..." : "Suscribirme"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
